import type * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SurveyQuestion } from '../types';
import { denormalizeSurveyFormFromFields, v1ToV2 } from '../utils/transform';

/**
 * Returns a callback that converts the current legacy `surveyForm.surveyParts` (a flat
 * `SurveyQuestion[]`) in-memory into the V2 sectioned shape — a headerless wrapper
 * section around the legacy questions, plus a fresh empty section.
 *
 * V1 surveyParts items loaded from the BE are reference-only stubs (`_type`,
 * `fieldId`, `id`, `visibleWhen`) — labels, types, and options live on
 * `customForm.fields[]`. Wrapping the stubs into V2 sections as-is leaves
 * every question rendering as "Untitled Question" with a blank type selector
 * and missing options. Pipe the wrapped result through
 * `denormalizeSurveyFormFromFields` so each question gets joined with its
 * matching `customForm.fields` entry before we write back to RHF.
 *
 * Used by V1's `+ Add Section` button in `AdditionalQuestions` to upgrade a legacy
 * survey to V2 without a save-roundtrip. The parent (`CustomSurvey`) re-evaluates
 * `hasV2Shape` and swaps to the V2 editor on the next render. `shouldDirty: true`
 * ensures the unsaved-changes warning surfaces immediately.
 */
export function useV1ToV2Transform(): () => void {
	const { getValues, setValue } = useFormContext();
	return useCallback(() => {
		const current = (getValues('surveyForm.surveyParts') ?? []) as SurveyQuestion[];
		const sections = v1ToV2(current);
		const fields = (getValues('customForm.fields') ?? []) as Api.IFormField<string>[];
		const { surveyParts: denormalizedParts } = denormalizeSurveyFormFromFields(
			{ isEnabled: true, surveyParts: sections } as unknown as Api.SurveyForm,
			fields
		);
		setValue('surveyForm.surveyParts', denormalizedParts, { shouldDirty: true });
	}, [getValues, setValue]);
}
